import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../../api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';
import toast from 'react-hot-toast';
import styles from './Reports.module.css';

// ── helpers ───────────────────────────────────────────────────
const fmt    = n => Number(n || 0).toLocaleString('en-IN');
const fmtCr  = n => n >= 10000000 ? `₹${(n/10000000).toFixed(2)}Cr` : n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${fmt(n)}`;
const pct    = (a, b) => b > 0 ? Math.round(a / b * 1000) / 10 : 0;

// ── MOCK fallback data (shown when API unavailable) ───────────
const MOCK = {
  assets:      { total:42, operational:34, maintenance:5, breakdown:3 },
  work_orders: { total:128, open_count:12, cost_ytd:284500 },
  compliance:  { total:18, valid:15, rate:83.3 },
  monthly: [
    { month:'Aug', pm:4,  bd:2, cost:28000 },
    { month:'Sep', pm:6,  bd:3, cost:41200 },
    { month:'Oct', pm:5,  bd:1, cost:18500 },
    { month:'Nov', pm:8,  bd:4, cost:67800 },
    { month:'Dec', pm:7,  bd:2, cost:32100 },
    { month:'Jan', pm:9,  bd:5, cost:89400 },
    { month:'Feb', pm:6,  bd:3, cost:45200 },
    { month:'Mar', pm:11, bd:2, cost:38600 },
  ],
};

// ── KPI Card ──────────────────────────────────────────────────
function KPI({ label, value, sub, trend, trendUp, color='blue' }) {
  return (
    <div className={`${styles.kpi_card} ${styles[`kpi_${color}`]}`}>
      <div className={styles.kpi_label}>{label}</div>
      <div className={styles.kpi_value}>{value}</div>
      {sub   && <div className={styles.kpi_sub}>{sub}</div>}
      {trend && <div className={trendUp ? styles.kpi_trend_up : styles.kpi_trend_down}>{trend}</div>}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────
function Progress({ label, actual, target, color }) {
  const over = actual >= target;
  return (
    <div className={styles.progress_row}>
      <div className={styles.progress_header}>
        <span className={styles.progress_label}>{label} <span style={{color:'#94a3b8',fontWeight:400,fontSize:12}}>(target {target}%)</span></span>
        <span className={styles.progress_val} style={{color: over ? '#22c55e' : actual > target*0.8 ? '#f59e0b' : '#ef4444'}}>
          {actual}%
        </span>
      </div>
      <div className={styles.progress_track}>
        <div className={styles.progress_fill} style={{
          width: `${Math.min(actual, 100)}%`,
          background: over ? '#22c55e' : actual > target * 0.8 ? '#f59e0b' : '#ef4444',
        }}/>
      </div>
    </div>
  );
}

// ── Custom tooltip ────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,padding:'10px 14px',fontSize:12,boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
      <div style={{fontWeight:700,color:'#1e2a3b',marginBottom:6}}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{color:p.color,display:'flex',gap:8,justifyContent:'space-between'}}>
          <span>{p.name}:</span><strong>{p.name==='Cost' ? fmtCr(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────
export default function ReportsPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [useMock, setUseMock] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setUseMock(false);
    reportsAPI.summary()
      .then(r => { setData(r.data); })
      .catch(err => {
        setData(MOCK);
        setUseMock(true);
        if (!err.response) {
          setError('FastAPI server not running. Start it with: uvicorn main:app --reload (in the backend folder)');
        } else {
          const detail = err.response?.data?.detail || err.message || 'Unknown error';
          setError(`API error ${err.response.status}: ${detail}`);
        }
        console.error('[Reports API]', err.response?.data || err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.toolbar_right}><span className={styles.total_badge}>Loading…</span></div>
      </div>
      <div className={styles.reports_body}>
        <div className={styles.kpi_row}>
          {[1,2,3,4].map(i=>(
            <div key={i} className={styles.kpi_card} style={{animation:'pulse 1.5s ease infinite'}}>
              <div style={{height:12,width:'60%',background:'#e2e8f0',borderRadius:6,marginBottom:12}}/>
              <div style={{height:28,width:'40%',background:'#e2e8f0',borderRadius:6}}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const { assets, work_orders: wo, compliance, monthly } = data || MOCK;
  const uptime   = pct(assets.operational, assets.total);
  const pmRate   = monthly.length ? pct(monthly.reduce((a,m)=>a+m.pm,0), monthly.reduce((a,m)=>a+(m.pm+m.bd),0)) : 0;
  const pieData  = [
    { name:'Operational', value: assets.operational, color:'#22c55e' },
    { name:'Maintenance',  value: assets.maintenance, color:'#f59e0b' },
    { name:'Breakdown',    value: assets.breakdown,   color:'#ef4444' },
    { name:'Retired',      value: (assets.total - assets.operational - assets.maintenance - assets.breakdown), color:'#94a3b8' },
  ].filter(d => d.value > 0);

  return (
    <div className={styles.page}>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <span style={{fontWeight:700,fontSize:15,color:'#1e2a3b'}}>Reports & Analytics</span>

        <div className={styles.export_bar} style={{marginLeft:'auto',paddingBottom:0}}>
          <button className={styles.export_btn} onClick={()=>toast('PDF export coming soon 📄')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Download PDF
          </button>
          <button className={styles.export_btn} onClick={()=>toast('Excel export coming soon 📊')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
            Download XLSX
          </button>
          <button className={styles.export_btn} onClick={load}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.reports_body}>

        {/* Error / warning banner */}
        {useMock && (
          <div style={{background: error && error.includes('500') ? '#fef2f2' : '#fff7ed', border:`1px solid ${error && error.includes('500') ? '#fecaca' : '#fed7aa'}`,borderRadius:10,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'flex-start',gap:10,fontSize:13}}>
            <span style={{fontSize:18,flexShrink:0}}>{error && error.includes('not running') ? '🔴' : '⚠️'}</span>
            <div style={{flex:1}}>
              <strong style={{color:'#c2410c',display:'block',marginBottom:3}}>
                {error && error.includes('not running') ? 'FastAPI server is not running' : 'API error — showing sample data'}
              </strong>
              <span style={{color:'#92400e',fontSize:12,lineHeight:1.5}}>{error}</span>
              {error && error.includes('not running') && (
                <div style={{marginTop:8,background:'#1e2a3b',color:'#e2e8f0',borderRadius:6,padding:'8px 12px',fontFamily:'monospace',fontSize:12}}>
                  cd backend &nbsp;&nbsp; uvicorn main:app --reload
                </div>
              )}
            </div>
            <button onClick={load} style={{flexShrink:0,padding:'5px 14px',background:'#c2410c',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>Retry</button>
          </div>
        )}

        {/* KPI Row */}
        <div className={styles.kpi_row}>
          <KPI color="blue"  label="Total Assets"     value={fmt(assets.total)}       sub={`${assets.breakdown} in breakdown`} />
          <KPI color="green" label="Uptime"           value={`${uptime}%`}            sub={`${assets.operational} operational`} trend={uptime >= 90 ? '▲ On target' : '▼ Below target'} trendUp={uptime >= 90} />
          <KPI color="amber" label="Compliance Rate"  value={`${compliance.rate}%`}   sub={`${compliance.valid} of ${compliance.total} certs valid`} />
          <KPI color="blue"  label="Maint. Cost YTD"  value={fmtCr(wo.cost_ytd)}      sub={`${wo.open_count} open work orders`} />
        </div>

        {/* Charts row 1 */}
        <div className={styles.charts_row}>
          {/* Bar chart */}
          <div className={styles.chart_card}>
            <div className={styles.chart_header}>
              <div className={styles.chart_title}>Monthly Work Orders</div>
              <div className={styles.chart_sub}>Preventive Maintenance vs Breakdowns — last 8 months</div>
            </div>
            <div className={styles.chart_body}>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={monthly} margin={{top:4,right:8,left:-24,bottom:0}} barGap={3}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                  <XAxis dataKey="month" tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<CustomTooltip/>}/>
                  <Legend iconSize={8} iconType="circle" wrapperStyle={{fontSize:12,paddingTop:8}}/>
                  <Bar dataKey="pm" name="Preventive" fill="#1a56db" radius={[4,4,0,0]} maxBarSize={24}/>
                  <Bar dataKey="bd" name="Breakdown"  fill="#ef4444" radius={[4,4,0,0]} maxBarSize={24}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* KPI vs Target */}
          <div className={styles.chart_card}>
            <div className={styles.chart_header}>
              <div className={styles.chart_title}>KPI vs Targets</div>
              <div className={styles.chart_sub}>Current performance</div>
            </div>
            <div className={styles.chart_body}>
              <Progress label="Asset Uptime"     actual={uptime}            target={90}  />
              <Progress label="PM Rate"          actual={pmRate}            target={80}  />
              <Progress label="Compliance Rate"  actual={compliance.rate}   target={100} />
              <Progress label="WO Closure Rate"  actual={Math.round(pct(wo.total - wo.open_count, wo.total))} target={85} />
            </div>
          </div>
        </div>

        {/* Charts row 2 */}
        <div className={styles.bottom_row}>
          {/* Cost trend line chart */}
          <div className={styles.chart_card}>
            <div className={styles.chart_header}>
              <div className={styles.chart_title}>Maintenance Cost Trend</div>
              <div className={styles.chart_sub}>Monthly spend in ₹</div>
            </div>
            <div className={styles.chart_body}>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={monthly} margin={{top:4,right:8,left:-16,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                  <XAxis dataKey="month" tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`}/>
                  <Tooltip content={<CustomTooltip/>}/>
                  <Line type="monotone" dataKey="cost" name="Cost" stroke="#1a56db" strokeWidth={2.5} dot={{fill:'#1a56db',r:4,strokeWidth:0}} activeDot={{r:6}}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Asset status donut */}
          <div className={styles.chart_card}>
            <div className={styles.chart_header}>
              <div className={styles.chart_title}>Asset Status Breakdown</div>
              <div className={styles.chart_sub}>Distribution by status</div>
            </div>
            <div className={styles.chart_body} style={{display:'flex',alignItems:'center',gap:16}}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={42} outerRadius={65} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="none"/>)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{flex:1}}>
                {pieData.map(d => (
                  <div key={d.name} style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,fontSize:13}}>
                    <div style={{width:10,height:10,borderRadius:'50%',background:d.color,flexShrink:0}}/>
                    <span style={{flex:1,color:'#4a5568'}}>{d.name}</span>
                    <strong style={{color:'#1e2a3b'}}>{d.value}</strong>
                  </div>
                ))}
                <div style={{marginTop:10,paddingTop:8,borderTop:'1px solid #e2e8f0',fontSize:12,color:'#64748b'}}>
                  Total: <strong style={{color:'#1e2a3b'}}>{assets.total}</strong> assets
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary table */}
        <div className={styles.chart_card} style={{marginTop:16}}>
          <div className={styles.chart_header}>
            <div className={styles.chart_title}>Monthly Summary Table</div>
            <div className={styles.chart_sub}>PM, breakdown and cost by month</div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{background:'#f8f9fc',borderBottom:'1px solid #e2e8f0'}}>
                  {['Month','Preventive','Breakdown','Total WOs','Cost','PM Rate'].map(h => (
                    <th key={h} style={{padding:'10px 16px',textAlign:'left',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#94a3b8',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthly.map((row, i) => {
                  const total = row.pm + row.bd;
                  const rate  = pct(row.pm, total);
                  return (
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                      <td style={{padding:'10px 16px',fontWeight:600,color:'#1e2a3b'}}>{row.month}</td>
                      <td style={{padding:'10px 16px'}}>
                        <span style={{background:'#eff4ff',color:'#1a56db',fontSize:12,fontWeight:600,padding:'2px 8px',borderRadius:10}}>{row.pm}</span>
                      </td>
                      <td style={{padding:'10px 16px'}}>
                        <span style={{background: row.bd > 3 ? '#fee2e2' : '#fff7ed', color: row.bd > 3 ? '#b91c1c' : '#c2410c', fontSize:12,fontWeight:600,padding:'2px 8px',borderRadius:10}}>{row.bd}</span>
                      </td>
                      <td style={{padding:'10px 16px',color:'#4a5568'}}>{total}</td>
                      <td style={{padding:'10px 16px',fontWeight:600,color:'#1e2a3b'}}>{fmtCr(row.cost)}</td>
                      <td style={{padding:'10px 16px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{flex:1,height:6,background:'#f1f5f9',borderRadius:10,overflow:'hidden',minWidth:60}}>
                            <div style={{height:'100%',background: rate>=80?'#22c55e':rate>=60?'#f59e0b':'#ef4444',width:`${rate}%`,borderRadius:10}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:600,color:'#4a5568',minWidth:32}}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
